import React, { useState, useEffect, useCallback } from "react";
import { Stock, StockRecipe, StockType, FullBOMItem } from "../types";
import {
  getStocks,
  getMamulStocks,
  getYariMamulStocks,
  getHammaddeStocks,
  getStockRecipes,
  getStockRecipesByParent,
  getFullBOM,
  createStockRecipe,
  updateStockRecipe,
  deleteStockRecipe,
  consumeForProduction,
} from "../services/api";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./ProductTree.css";

// Stok Tipi etiketleri
const stockTypeLabels: Record<StockType, string> = {
  [StockType.Hammadde]: "Hammadde",
  [StockType.YariMamul]: "Yarı Mamül",
  [StockType.Mamul]: "Mamül",
};

// Stok Tipi renkleri
const stockTypeColors: Record<StockType, { bg: string; text: string }> = {
  [StockType.Hammadde]: { bg: "#dbeafe", text: "#1d4ed8" },
  [StockType.YariMamul]: { bg: "#fef3c7", text: "#d97706" },
  [StockType.Mamul]: { bg: "#d1fae5", text: "#047857" },
};

const ProductTree: React.FC = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [mamulStocks, setMamulStocks] = useState<Stock[]>([]);
  const [yariMamulStocks, setYariMamulStocks] = useState<Stock[]>([]);
  const [hammaddeStocks, setHammaddeStocks] = useState<Stock[]>([]);
  const [recipes, setRecipes] = useState<StockRecipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Selected product for tree view
  const [selectedMamul, setSelectedMamul] = useState<Stock | null>(null);
  const [fullBOM, setFullBOM] = useState<FullBOMItem | null>(null);
  const [productRecipes, setProductRecipes] = useState<StockRecipe[]>([]);

  // Recipe Modal states
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<StockRecipe | null>(null);
  const [recipeFormData, setRecipeFormData] = useState({
    parentStockId: "",
    childStockId: "",
    requiredQuantity: 1,
    wastagePercentage: 0,
    description: "",
    sortOrder: 1,
  });

  // Production Modal states
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [productionFormData, setProductionFormData] = useState({
    stockId: "",
    quantity: 1,
    referenceNo: "",
    movedBy: "",
    relatedOrderId: undefined as string | undefined,
  });

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  // Data loading
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [allStocks, mamul, yariMamul, hammadde, allRecipes] =
        await Promise.all([
          getStocks(),
          getMamulStocks(),
          getYariMamulStocks(),
          getHammaddeStocks(),
          getStockRecipes(),
        ]);
      setStocks(allStocks.data || []);
      setMamulStocks(mamul.data || []);
      setYariMamulStocks(yariMamul.data || []);
      setHammaddeStocks(hammadde.data || []);
      setRecipes(allRecipes.data || []);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Veriler yüklenirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load product tree when a mamul is selected
  const loadProductTree = async (mamul: Stock) => {
    setSelectedMamul(mamul);
    try {
      setLoading(true);
      const [bomRes, recipesRes] = await Promise.all([
        getFullBOM(mamul.id),
        getStockRecipesByParent(mamul.id),
      ]);
      setFullBOM(bomRes.data || null);
      setProductRecipes(recipesRes.data || []);
    } catch (error) {
      console.error("Ürün ağacı yüklenemedi:", error);
      setFullBOM(null);
      setProductRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  // Recipe CRUD
  const openAddRecipeModal = (parentStock?: Stock) => {
    setEditingRecipe(null);
    setRecipeFormData({
      parentStockId: parentStock?.id || selectedMamul?.id || "",
      childStockId: "",
      requiredQuantity: 1,
      wastagePercentage: 0,
      description: "",
      sortOrder: 1,
    });
    setIsRecipeModalOpen(true);
  };

  const openEditRecipeModal = (recipe: StockRecipe) => {
    setEditingRecipe(recipe);
    setRecipeFormData({
      parentStockId: recipe.parentStockId,
      childStockId: recipe.childStockId,
      requiredQuantity: recipe.requiredQuantity,
      wastagePercentage: recipe.wastagePercentage || 0,
      description: recipe.description || "",
      sortOrder: recipe.sortOrder || 1,
    });
    setIsRecipeModalOpen(true);
  };

  const handleRecipeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recipeFormData.parentStockId || !recipeFormData.childStockId) {
      setAlertModal({
        isOpen: true,
        message: "Ana ürün ve malzeme seçimi zorunludur!",
        type: "warning",
      });
      return;
    }

    if (recipeFormData.parentStockId === recipeFormData.childStockId) {
      setAlertModal({
        isOpen: true,
        message: "Ana ürün ve malzeme aynı olamaz!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      if (editingRecipe) {
        await updateStockRecipe(editingRecipe.id, {
          id: editingRecipe.id,
          ...recipeFormData,
          isActive: true,
        });
        setAlertModal({
          isOpen: true,
          message: "Reçete başarıyla güncellendi!",
          type: "success",
        });
      } else {
        await createStockRecipe(recipeFormData);
        setAlertModal({
          isOpen: true,
          message: "Reçete başarıyla oluşturuldu!",
          type: "success",
        });
      }
      setIsRecipeModalOpen(false);
      loadData();
      if (selectedMamul) {
        loadProductTree(selectedMamul);
      }
    } catch (error) {
      console.error("Reçete kaydedilemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Reçete kaydedilirken hata oluştu!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecipe = (recipe: StockRecipe) => {
    const parentName =
      stocks.find((s) => s.id === recipe.parentStockId)?.productName || "Ürün";
    const childName =
      stocks.find((s) => s.id === recipe.childStockId)?.productName ||
      "Malzeme";

    setConfirmModal({
      isOpen: true,
      message: `"${parentName}" için "${childName}" malzeme ilişkisini silmek istediğinize emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await deleteStockRecipe(recipe.id);
          setAlertModal({
            isOpen: true,
            message: "Reçete başarıyla silindi!",
            type: "success",
          });
          loadData();
          if (selectedMamul) {
            loadProductTree(selectedMamul);
          }
        } catch (error) {
          console.error("Reçete silinemedi:", error);
          setAlertModal({
            isOpen: true,
            message: "Reçete silinirken hata oluştu!",
            type: "error",
          });
        } finally {
          setLoading(false);
          setConfirmModal({ ...confirmModal, isOpen: false });
        }
      },
    });
  };

  // Production
  const openProductionModal = (stock: Stock) => {
    setProductionFormData({
      stockId: stock.id,
      quantity: 1,
      referenceNo: `URE-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      movedBy: "",
      relatedOrderId: undefined,
    });
    setIsProductionModalOpen(true);
  };

  const handleProductionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (productionFormData.quantity <= 0) {
      setAlertModal({
        isOpen: true,
        message: "Üretim miktarı 0'dan büyük olmalıdır!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      await consumeForProduction(productionFormData);
      setAlertModal({
        isOpen: true,
        message:
          "Üretim başarıyla gerçekleştirildi! Malzemeler stoktan düşüldü.",
        type: "success",
      });
      setIsProductionModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Üretim gerçekleştirilemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Üretim sırasında hata oluştu! Yeterli stok olmayabilir.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter mamul stocks
  const filteredMamulStocks = mamulStocks.filter(
    (stock) =>
      stock.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stock.productCode.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Get available child stocks for recipe (exclude parent and its ancestors)
  const getAvailableChildStocks = () => {
    const parentStock = stocks.find(
      (s) => s.id === recipeFormData.parentStockId,
    );
    if (!parentStock) return [];

    // For Mamul, can add YariMamul or Hammadde
    // For YariMamul, can add Hammadde
    if (parentStock.stockType === StockType.Mamul) {
      return [...yariMamulStocks, ...hammaddeStocks].filter(
        (s) => s.id !== recipeFormData.parentStockId,
      );
    } else if (parentStock.stockType === StockType.YariMamul) {
      return hammaddeStocks.filter(
        (s) => s.id !== recipeFormData.parentStockId,
      );
    }
    return [];
  };

  // Recursive BOM Tree Component
  const BOMTreeItem: React.FC<{ item: FullBOMItem; level: number }> = ({
    item,
    level,
  }) => {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = item.children && item.children.length > 0;
    const stockType = item.stock.stockType || StockType.Hammadde;
    const colors = stockTypeColors[stockType];

    return (
      <div className="bom-tree-item" style={{ marginLeft: level * 24 }}>
        <div className="bom-tree-row">
          {hasChildren && (
            <button
              className="bom-expand-btn"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "▼" : "▶"}
            </button>
          )}
          {!hasChildren && <span className="bom-expand-placeholder" />}

          <span
            className="bom-type-badge"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {stockTypeLabels[stockType]}
          </span>

          <span className="bom-stock-code">{item.stock.productCode}</span>
          <span className="bom-stock-name">{item.stock.productName}</span>

          <span className="bom-quantity">
            {item.requiredQuantity} {item.stock.unit}
            {item.wastagePercentage && item.wastagePercentage > 0 && (
              <span className="bom-wastage">
                (+{item.wastagePercentage}% fire)
              </span>
            )}
          </span>

          <span className="bom-current-stock">
            Mevcut: {item.stock.currentStock} {item.stock.unit}
          </span>
        </div>

        {expanded && hasChildren && (
          <div className="bom-tree-children">
            {item.children!.map((child, index) => (
              <BOMTreeItem key={index} item={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading && stocks.length === 0) {
    return <PageLoader message="Ürün ağacı verileri yükleniyor..." />;
  }

  return (
    <div className="product-tree-container">
      {/* Header */}
      <div className="product-tree-header">
        <div className="product-tree-header-content">
          <h1>
            <span className="product-tree-header-icon">🌳</span>
            Ürün Ağacı (BOM)
          </h1>
          <p className="product-tree-subtitle">
            Mamül, yarı mamül ve hammadde ilişkilerini yönetin
          </p>
        </div>
        <div className="product-tree-header-actions">
          <button
            className="btn btn-primary"
            onClick={() => openAddRecipeModal()}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Yeni Reçete
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="product-tree-content">
        {/* Left Panel - Product List */}
        <div className="product-tree-left-panel">
          <div className="panel-header">
            <h3>📦 Mamül Listesi</h3>
            <div className="panel-search">
              <input
                type="text"
                placeholder="Mamül ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="mamul-list">
            {filteredMamulStocks.length === 0 ? (
              <div className="empty-list">
                <p>Henüz mamül tanımlanmamış.</p>
                <small>Stok yönetiminden mamül ekleyebilirsiniz.</small>
              </div>
            ) : (
              filteredMamulStocks.map((mamul) => (
                <div
                  key={mamul.id}
                  className={`mamul-item ${selectedMamul?.id === mamul.id ? "selected" : ""}`}
                  onClick={() => loadProductTree(mamul)}
                >
                  <div className="mamul-item-header">
                    <span className="mamul-code">{mamul.productCode}</span>
                    <span className="mamul-stock">
                      {mamul.currentStock} {mamul.unit}
                    </span>
                  </div>
                  <div className="mamul-name">{mamul.productName}</div>
                  {mamul.description && (
                    <div className="mamul-description">{mamul.description}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Product Tree */}
        <div className="product-tree-right-panel">
          {selectedMamul ? (
            <>
              <div className="panel-header">
                <div className="selected-product-info">
                  <h3>🔧 {selectedMamul.productName}</h3>
                  <span className="selected-product-code">
                    {selectedMamul.productCode}
                  </span>
                </div>
                <div className="panel-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => openAddRecipeModal(selectedMamul)}
                  >
                    + Malzeme Ekle
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => openProductionModal(selectedMamul)}
                  >
                    🏭 Üretim Yap
                  </button>
                </div>
              </div>

              {/* BOM Tree View */}
              {fullBOM ? (
                <div className="bom-tree-container">
                  <BOMTreeItem item={fullBOM} level={0} />
                </div>
              ) : (
                <div className="empty-tree">
                  <p>Bu mamül için henüz malzeme reçetesi tanımlanmamış.</p>
                  <button onClick={() => openAddRecipeModal(selectedMamul)}>
                    + İlk Malzemeyi Ekle
                  </button>
                </div>
              )}

              {/* Recipe List */}
              <div className="recipe-list-section">
                <h4>📋 Reçete Detayları</h4>
                {productRecipes.length === 0 ? (
                  <p className="no-recipes">Henüz reçete bulunmuyor.</p>
                ) : (
                  <table className="recipe-table">
                    <thead>
                      <tr>
                        <th>Malzeme</th>
                        <th>Tip</th>
                        <th>Miktar</th>
                        <th>Fire %</th>
                        <th>Açıklama</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productRecipes.map((recipe) => {
                        const childStock = stocks.find(
                          (s) => s.id === recipe.childStockId,
                        );
                        const stockType =
                          childStock?.stockType || StockType.Hammadde;
                        const colors = stockTypeColors[stockType];

                        return (
                          <tr key={recipe.id}>
                            <td>
                              <div className="recipe-material">
                                <span className="material-code">
                                  {childStock?.productCode}
                                </span>
                                <span className="material-name">
                                  {childStock?.productName}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span
                                className="recipe-type-badge"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                }}
                              >
                                {stockTypeLabels[stockType]}
                              </span>
                            </td>
                            <td>
                              {recipe.requiredQuantity} {childStock?.unit}
                            </td>
                            <td>{recipe.wastagePercentage || 0}%</td>
                            <td>{recipe.description || "-"}</td>
                            <td>
                              <div className="recipe-actions">
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => openEditRecipeModal(recipe)}
                                  title="Düzenle"
                                >
                                  ✏️
                                </button>
                                <button
                                  className="btn btn-sm btn-soft-danger"
                                  onClick={() => handleDeleteRecipe(recipe)}
                                  title="Sil"
                                >
                                  🗑️
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-icon">👈</div>
              <h3>Bir Mamül Seçin</h3>
              <p>
                Sol panelden bir mamül seçerek ürün ağacını
                görüntüleyebilirsiniz.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Section */}
      <div className="product-tree-summary">
        <div className="summary-card">
          <div className="summary-icon mamul">📦</div>
          <div className="summary-info">
            <span className="summary-count">{mamulStocks.length}</span>
            <span className="summary-label">Mamül</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon yarimamul">🔧</div>
          <div className="summary-info">
            <span className="summary-count">{yariMamulStocks.length}</span>
            <span className="summary-label">Yarı Mamül</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon hammadde">🧵</div>
          <div className="summary-info">
            <span className="summary-count">{hammaddeStocks.length}</span>
            <span className="summary-label">Hammadde</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon recipe">📋</div>
          <div className="summary-info">
            <span className="summary-count">{recipes.length}</span>
            <span className="summary-label">Reçete</span>
          </div>
        </div>
      </div>

      {/* Recipe Modal */}
      {isRecipeModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsRecipeModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRecipe ? "Reçete Düzenle" : "Yeni Reçete Ekle"}</h2>
              <button
                className="modal-close"
                onClick={() => setIsRecipeModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleRecipeSubmit} className="modal-form">
              <div className="form-group">
                <label>Ana Ürün (Üretilecek) *</label>
                <select
                  value={recipeFormData.parentStockId}
                  onChange={(e) =>
                    setRecipeFormData({
                      ...recipeFormData,
                      parentStockId: e.target.value,
                      childStockId: "", // Reset child when parent changes
                    })
                  }
                  required
                >
                  <option value="">Ana ürün seçin</option>
                  <optgroup label="Mamüller">
                    {mamulStocks.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.productCode} - {stock.productName}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Yarı Mamüller">
                    {yariMamulStocks.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.productCode} - {stock.productName}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className="form-group">
                <label>Malzeme (Kullanılacak) *</label>
                <select
                  value={recipeFormData.childStockId}
                  onChange={(e) =>
                    setRecipeFormData({
                      ...recipeFormData,
                      childStockId: e.target.value,
                    })
                  }
                  required
                  disabled={!recipeFormData.parentStockId}
                >
                  <option value="">Malzeme seçin</option>
                  {getAvailableChildStocks().map((stock) => {
                    const stockType = stock.stockType || StockType.Hammadde;
                    return (
                      <option key={stock.id} value={stock.id}>
                        [{stockTypeLabels[stockType]}] {stock.productCode} -{" "}
                        {stock.productName}
                      </option>
                    );
                  })}
                </select>
                {!recipeFormData.parentStockId && (
                  <small className="form-hint">Önce ana ürün seçin</small>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Gerekli Miktar *</label>
                  <input
                    type="number"
                    value={recipeFormData.requiredQuantity}
                    onChange={(e) =>
                      setRecipeFormData({
                        ...recipeFormData,
                        requiredQuantity: Number(e.target.value),
                      })
                    }
                    min={0.01}
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Fire Yüzdesi (%)</label>
                  <input
                    type="number"
                    value={recipeFormData.wastagePercentage}
                    onChange={(e) =>
                      setRecipeFormData({
                        ...recipeFormData,
                        wastagePercentage: Number(e.target.value),
                      })
                    }
                    min={0}
                    max={100}
                    step="0.1"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={recipeFormData.description}
                  onChange={(e) =>
                    setRecipeFormData({
                      ...recipeFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Örn: 1 adet gömlek için 2.5 metre kumaş gerekli"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Sıra No</label>
                <input
                  type="number"
                  value={recipeFormData.sortOrder}
                  onChange={(e) =>
                    setRecipeFormData({
                      ...recipeFormData,
                      sortOrder: Number(e.target.value),
                    })
                  }
                  min={1}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsRecipeModalOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading
                    ? "Kaydediliyor..."
                    : editingRecipe
                      ? "Güncelle"
                      : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Production Modal */}
      {isProductionModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setIsProductionModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏭 Üretim Yap</h2>
              <button
                className="modal-close"
                onClick={() => setIsProductionModalOpen(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleProductionSubmit} className="modal-form">
              <div className="production-info">
                <p>
                  <strong>{selectedMamul?.productName}</strong> üretimi için
                  reçetedeki malzemeler otomatik olarak stoktan düşülecektir.
                </p>
              </div>

              <div className="form-group">
                <label>Üretim Miktarı *</label>
                <input
                  type="number"
                  value={productionFormData.quantity}
                  onChange={(e) =>
                    setProductionFormData({
                      ...productionFormData,
                      quantity: Number(e.target.value),
                    })
                  }
                  min={1}
                  step="1"
                  required
                />
              </div>

              <div className="form-group">
                <label>Referans No</label>
                <input
                  type="text"
                  value={productionFormData.referenceNo}
                  onChange={(e) =>
                    setProductionFormData({
                      ...productionFormData,
                      referenceNo: e.target.value,
                    })
                  }
                  placeholder="Örn: URE-2026-001"
                />
              </div>

              <div className="form-group">
                <label>Üretimi Yapan</label>
                <input
                  type="text"
                  value={productionFormData.movedBy}
                  onChange={(e) =>
                    setProductionFormData({
                      ...productionFormData,
                      movedBy: e.target.value,
                    })
                  }
                  placeholder="Personel adı"
                />
              </div>

              <div className="form-group">
                <label>İlgili Sipariş No (Opsiyonel)</label>
                <input
                  type="text"
                  value={productionFormData.relatedOrderId || ""}
                  onChange={(e) =>
                    setProductionFormData({
                      ...productionFormData,
                      relatedOrderId: e.target.value || undefined,
                    })
                  }
                  placeholder="Sipariş ID"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsProductionModalOpen(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? "Üretiliyor..." : "Üretimi Başlat"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default ProductTree;
